import { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';

import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';

import { object, string } from 'yup';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';

import messages from './messages';

import TextInput from 'src/components/TextInput';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { forceString } from 'src/utils/datetime';

interface Props {
  file?: UCM.VerificationCancellationProofsType | null;
  cancellationProof?: UCM.CancellationProofType;
  handleApprove: (params: Record<string, string>) => void;
  handleReject: (params: Record<string, string>) => void;
  handleRetry: (params: Record<string, string>) => void;
  loading: boolean;
}

interface CancellationProofState {
  rejectionReason?: string | null;
  adminComment?: string | null;
}

export default function VerificationCancellationProof({
  file,
  cancellationProof,
  handleApprove,
  handleReject,
  handleRetry,
  loading,
}: Props) {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const [isCopied, setCopied] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const initialValues: CancellationProofState = useMemo(
    () => ({
      rejectionReason: file?.rejectionReason,
      adminComment: file?.adminComment,
    }),
    [file],
  );

  useEffect(() => {
    if (isCopied) setTimeout(() => setCopied(false), 2000);
  }, [isCopied]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnChange: validateOnChange,
    validationSchema: object().shape({
      rejectionReason: file?.proof ? string() : string().required(),
      adminComment: string().notRequired(),
    }),
    onSubmit: () => {},
  });

  const { values, setFieldValue, resetForm } = formik;

  useEffect(() => {
    if (file?.uuid && validateOnChange) {
      resetForm();
      setValidateOnChange(false);
    }
  }, [file?.uuid]);

  const handleChange = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      name: keyof CancellationProofState,
    ) => {
      setFieldValue(name, event.target.value);
    },
    [],
  );

  const handleCopyText = useCallback(() => {
    try {
      navigator.clipboard
        .writeText(cancellationProof?.healthInsurance?.number || '-')
        .then(() => setCopied(true));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy: ', err);
    }
  }, [cancellationProof]);

  const handlePressApprove = useCallback(async () => {
    handleApprove({
      admin_comment: values.adminComment ?? '',
    });
  }, [values.adminComment, handleApprove]);

  const handlePressDecline = useCallback(() => {
    handleReject({
      rejection_reason: values.rejectionReason ?? '',
      admin_comment: values.adminComment ?? '',
    });
  }, [values.rejectionReason, values.adminComment, handleReject]);

  const handlePressRetry = useCallback(() => {
    handleRetry({
      rejection_reason: values.rejectionReason ?? '',
      admin_comment: values.adminComment ?? '',
    });
  }, [values.rejectionReason, values.adminComment, handleRetry]);

  const disabledButtons = useMemo(() => {
    return !file || loading;
  }, [file, loading]);

  const cancellationShiftDates = useMemo(() => {
    if (!file?.proof) return file?.cancellationNotice?.shiftDates;

    return cancellationProof?.cancellationNotice?.shiftDates;
  }, [file?.proof, cancellationProof]);

  const handleCheckFormik = useCallback(
    async (callback: () => void) => {
      let isValid = true;
      if (!file?.proof) {
        setValidateOnChange(true);
        const errors = await formik.validateForm();
        isValid = isEmpty(errors);
      }

      if (!isValid) return false;

      callback();
    },
    [formik, file?.proof],
  );

  return (
    <Stack
      p={1.5}
      spacing={2}
      borderRadius={1.5}
      direction="column"
      bgcolor={theme.palette.common.white}
    >
      <Grid container spacing={1} alignItems="center">
        <Typography variant="h5">
          {formatMessage(messages.cancelIdLabel, {
            id: file?.cancellationNoticeId ?? '-',
          })}
        </Typography>
        <Separator />
        <Typography variant="h5">
          {formatMessage(messages.jobIdLabel, { id: file?.job?.id ?? '-' })}
        </Typography>
      </Grid>
      <Stack spacing={0.5}>
        {file?.proof && (
          <Typography variant="subtitle2">{file?.proof?.fileName}</Typography>
        )}
        <Grid container spacing={1} alignItems="center">
          {file?.proof && (
            <>
              <Typography variant="subtitle2">
                {formatMessage(messages.uploadedOnLabel, {
                  date: file?.createdAt ?? '-',
                })}
              </Typography>
              <Separator />
            </>
          )}
          <Typography variant="subtitle2">
            {formatMessage(messages.createdOnLabel, {
              date: file?.cancellationNotice.createdAt ?? '-',
            })}
          </Typography>
        </Grid>
      </Stack>
      <Stack spacing={0.5}>
        <Typography variant="subtitle2" color={theme.palette.grey[500]}>
          {formatMessage(messages.cancelledShiftLabel)}
        </Typography>
        <Grid container spacing={1.5} alignItems="center">
          {map(cancellationShiftDates, (shiftDate) => (
            <Grid key={shiftDate} container spacing={0.5} alignItems="center">
              <Separator size={3} color={theme.palette.common.black} />
              <Typography variant="subtitle2">
                {forceString(shiftDate)}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Stack>
      <LabelValue
        label={formatMessage(messages.cancelReasonLabel)}
        value={
          (cancellationProof?.cancellationNotice?.reason ||
            file?.cancellationNotice?.reason) ??
          '-'
        }
      />
      <LabelValue
        label={formatMessage(messages.userExplanationLabel)}
        value={
          (cancellationProof?.cancellationNotice?.explanation ||
            file?.cancellationNotice?.explanation) ??
          '-'
        }
      />

      {cancellationProof?.firstDayOfSickness &&
        cancellationProof?.healthInsurance && (
          <Stack
            p={2}
            width="50%"
            spacing={2}
            borderRadius={2}
            border={`thin solid ${theme.palette.grey[200]}`}
          >
            <Typography variant="h5">
              {formatMessage(messages.eAUTitle)}
            </Typography>

            <Grid size={12}>
              <LabelValue
                label={formatMessage(messages.healthInsuranceProviderLabel)}
                value={cancellationProof.healthInsurance.provider}
              />
            </Grid>

            <Grid size={12} container spacing={4} alignItems="center">
              <LabelValue
                label={formatMessage(messages.healthInsuranceNumberLabel)}
                value={cancellationProof.healthInsurance.number}
              />

              <Grid container spacing={1}>
                {isCopied ? (
                  <>
                    <TaskAltOutlinedIcon color="success" fontSize="small" />
                    <Typography variant="subtitle2" color="success">
                      {formatMessage(messages.numberCopiedSuccess)}
                    </Typography>
                  </>
                ) : (
                  <IconButton onClick={handleCopyText}>
                    <FileCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </Grid>
            </Grid>

            <Grid size={12}>
              <LabelValue
                label={formatMessage(messages.firstSicknoteDateLabel)}
                value={forceString(cancellationProof.firstDayOfSickness)}
              />
            </Grid>

            <Grid size={12}>
              <Link
                gap={1}
                color="info"
                display="flex"
                variant="body1"
                target="__blank"
                underline="always"
                alignItems="center"
                href="https://app.sv-meldeportal.de/de/login"
              >
                <Typography variant="subtitle2">
                  {formatMessage(messages.eauLinkMessage)}
                </Typography>

                <LinkOutlinedIcon fontSize="small" color="info" />
              </Link>
            </Grid>
          </Stack>
        )}

      <Grid container spacing={2}>
        <Grid size={6}>
          <TextInput
            fullWidth
            name="rejectionReason"
            value={values.rejectionReason}
            label={formatMessage(messages.rejectionReasonLabel)}
            onChange={(event) => handleChange(event, 'rejectionReason')}
            error={!!formik.errors.rejectionReason}
            helperText={formik.errors.rejectionReason}
          />
        </Grid>
        <Grid size={6}>
          <TextInput
            fullWidth
            name="adminComment"
            value={values.adminComment}
            label={formatMessage(messages.adminCommentLabel)}
            onChange={(event) => handleChange(event, 'adminComment')}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} justifyContent="flex-end" mt={1}>
        <ConfirmationDialog
          onConfirm={handlePressDecline}
          message={formatMessage(messages.rejectConfirmMessage)}
        >
          {(handleOpen) => (
            <LoadingButton
              variant="contained"
              color="error"
              disabled={disabledButtons}
              onClick={(event) => handleCheckFormik(() => handleOpen(event))}
              loading={loading}
            >
              {formatMessage(messages.declineButton)}
            </LoadingButton>
          )}
        </ConfirmationDialog>
        {file?.proof && (
          <ConfirmationDialog
            onConfirm={handlePressRetry}
            message={formatMessage(messages.rejectConfirmMessage)}
          >
            {(handleOpen) => (
              <LoadingButton
                variant={disabledButtons ? 'contained' : 'outlined'}
                color="error"
                disabled={disabledButtons}
                onClick={handleOpen}
                loading={loading}
              >
                {formatMessage(messages.retryButton)}
              </LoadingButton>
            )}
          </ConfirmationDialog>
        )}
        <ConfirmationDialog
          onConfirm={handlePressApprove}
          message={formatMessage(messages.approveConfirmMessage)}
        >
          {(handleOpen) => (
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={disabledButtons}
              onClick={handleOpen}
              loading={loading}
            >
              {formatMessage(messages.approveButton)}
            </LoadingButton>
          )}
        </ConfirmationDialog>
      </Grid>
    </Stack>
  );
}

interface LabelValueProps {
  label: string;
  value: string | React.ReactNode;
}

const LabelValue = ({ label, value }: LabelValueProps) => {
  const theme = useTheme();

  return (
    <Stack>
      <Typography variant="subtitle2" color={theme.palette.grey[500]}>
        {label}
      </Typography>
      <Typography variant="subtitle2">{value}</Typography>
    </Stack>
  );
};

interface SeparatorProps {
  size?: number;
  color?: string;
}

const Separator = ({ size = 5, color }: SeparatorProps) => {
  const theme = useTheme();

  return (
    <Box
      width={size}
      height={size}
      borderRadius={size}
      bgcolor={color || theme.palette.grey[500]}
    />
  );
};
